import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { Banner, Button, LoadingScreen, Wordmark } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDayShort, todayBerlin } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Field, Icon, RadioCard, SwitchRow, fieldClass } from '../../ui';

/**
 * What changed between this version and the one before it.
 *
 * `changes` is a loose object on the wire — `08` § 6 lets a change carry
 * different fields per kind — so the two the sticker sheet cares about are
 * named here rather than asserted at the call site.
 */
async function loadDiff(vid: string): Promise<{ kind: string; resourceId: string }[]> {
  const { data, error } = await api.GET('/admin/floor-plan-versions/{vid}/diff', {
    params: { path: { vid } },
  });
  if (error) throw error;
  return data.changes as { kind: string; resourceId: string }[];
}

/** § 8 — six stickers to an A4 sheet, and that is the only sheet in v1. */
const PER_SHEET = 6;

type Scope = 'new_changed' | 'all' | 'selection';

interface Printable {
  resourceId: string;
  deskCode: string;
  label: string;
}

/**
 * "Aufkleber drucken" (10-web-admin § 8).
 *
 * A modal over the Grundrisse list, because printing is something you do to a
 * plan rather than a place you go.
 *
 * The counts come from the server's own diff, so "neue und geänderte" is
 * exactly what the PDF will contain rather than a second guess at it. That was
 * worth nothing while the server compared a published version against itself
 * and answered "nothing changed": every live plan offered zero stickers,
 * including a version 1 whose desks have never been printed at all.
 */
export function Stickers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { planId = '' } = useParams();

  /**
   * § 8 — "Auswahl auf dem Plan" exists only where there is a selection to
   * carry, which is the editor. Router state rather than the query string: a
   * hundred ids do not belong in a URL, and the option is correctly gone when
   * somebody reloads the page and the selection with it.
   */
  const carried = (useLocation().state as { selection?: string[] } | null)?.selection;
  const selection = useMemo(() => carried ?? [], [carried]);

  const [scope, setScope] = useState<Scope | null>(null);
  const [cropMarks, setCropMarks] = useState(true);
  const [includeRoomsLockers, setIncludeRoomsLockers] = useState(false);
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const close = () => void navigate('/grundrisse');

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const plan = useQuery({
    queryKey: ['admin', 'floor-plans'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/floor-plans');
      if (error) throw error;
      return data.items;
    },
    select: (items) => items.find((item) => item.id === planId) ?? null,
  });

  const locations = useQuery({
    queryKey: ['admin', 'locations'],
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/locations', {});
      if (error) throw error;
      return data.items;
    },
  });

  const versionId = plan.data?.published?.id ?? plan.data?.draft?.id ?? null;

  const sheet = useQuery({
    queryKey: ['admin', 'sticker-sheet', versionId],
    enabled: versionId !== null,
    queryFn: async () => {
      const vid = versionId!;
      const preview = await api.GET('/admin/floor-plan-versions/{vid}/preview', {
        params: { path: { vid } },
      });
      if (preview.error) throw preview.error;
      const changes = await loadDiff(vid);
      // Only the two changes that alter what is printed (§ 8).
      const changed = new Set(
        changes
          .filter((change) => change.kind === 'added' || change.kind === 'relabelled')
          .map((change) => change.resourceId),
      );
      return { placements: preview.data.placements, changed };
    },
  });

  /** The same filter the server applies, so the counts cannot disagree. */
  const printable = useMemo((): Record<Scope, Printable[]> => {
    const all = (sheet.data?.placements ?? [])
      .filter((placement) => includeRoomsLockers || placement.kind === 'desk')
      .map((placement) => ({
        resourceId: placement.resourceId,
        deskCode: placement.deskCode ?? '',
        label: placement.label,
      }));
    return {
      all,
      new_changed: all.filter((item) => sheet.data?.changed.has(item.resourceId)),
      selection: all.filter((item) => selection.includes(item.resourceId)),
    };
  }, [sheet.data, includeRoomsLockers, selection]);

  /**
   * § 8 makes "nur neue und geänderte" the default because reprinting a whole
   * floor is the exception. Where there is nothing new it is not a default but
   * a dead end, so the dialog opens on the scope that has something in it —
   * which is what a version 1, or a plan republished without touching a
   * number, always is.
   */
  useEffect(() => {
    if (scope !== null || !sheet.data) return;
    setScope(printable.new_changed.length > 0 ? 'new_changed' : 'all');
  }, [scope, sheet.data, printable]);

  const chosen = printable[scope ?? 'new_changed'];
  const sheets = Math.ceil(chosen.length / PER_SHEET);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/admin/stickers', {
        body: {
          versionId: versionId!,
          scope: scope ?? 'new_changed',
          resourceIds: scope === 'selection' ? selection : undefined,
          format: '70x70' as const,
          sheet: 'a4_6' as const,
          cropMarks,
          includeRoomsLockers,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      // Opened after the await, so the tab is the direct result of the click
      // in every browser but Safari, where the sheet is one click away anyway.
      window.open(data.url, '_blank');
      close();
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  if (plan.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const version = plan.data?.published?.version ?? plan.data?.draft?.version ?? 0;
  const locationName = locations.data?.find((location) => location.id === plan.data?.locationId)?.name ?? '';
  const nothing = chosen.length === 0 || versionId === null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={close}
        className="absolute inset-0 cursor-default bg-[rgba(21,21,21,0.35)]"
      />
      {/* The board's modal, not the generic dialog: 1040 px, two columns, a
          24 px radius, and the options beside the sheet they describe. */}
      <form
        role="dialog"
        aria-label={t('admin.stickers.title')}
        onSubmit={(event) => {
          event.preventDefault();
          if (!nothing) create.mutate();
        }}
        className="relative grid w-[1040px] max-w-full grid-cols-[360px_minmax(0,1fr)] overflow-hidden rounded-[24px] bg-white shadow-[0_30px_80px_rgba(21,21,21,0.3)]"
      >
        <div className="border-line flex flex-col gap-4 border-r p-7">
          <div className="flex flex-col gap-1">
            <p className="font-heading text-ink text-[22px] font-bold">{t('admin.stickers.title')}</p>
            <p className="text-muted text-[13px]">
              {plan.data
                ? t('admin.stickers.subtitle', {
                    plan: t('admin.plans.cardName', { location: locationName, floor: plan.data.name }),
                    version,
                  })
                : ''}
            </p>
          </div>

          {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-label text-muted pb-1.5 uppercase">{t('admin.stickers.which')}</legend>
            <RadioCard
              name="sticker-scope"
              value="new_changed"
              checked={(scope ?? 'new_changed') === 'new_changed'}
              onChange={() => setScope('new_changed')}
              trailing={printable.new_changed.length}
            >
              {t('admin.stickers.scopeNewChanged')}
            </RadioCard>
            <RadioCard
              name="sticker-scope"
              value="all"
              checked={scope === 'all'}
              onChange={() => setScope('all')}
              trailing={printable.all.length}
            >
              {t('admin.stickers.scopeAll')}
            </RadioCard>
            {/* § 8 — offered always so the dialog reads the same everywhere,
                but only choosable where a selection came with it. */}
            <RadioCard
              name="sticker-scope"
              value="selection"
              checked={scope === 'selection'}
              disabled={selection.length === 0}
              onChange={() => setScope('selection')}
              trailing={printable.selection.length}
            >
              {t('admin.stickers.scopeSelection')}
            </RadioCard>
          </fieldset>

          <div className="grid grid-cols-2 gap-2.5">
            <Field label={t('admin.stickers.format')}>
              <select className={fieldClass} value="70x70" disabled>
                <option value="70x70">{t('admin.stickers.format70')}</option>
              </select>
            </Field>
            <Field label={t('admin.stickers.sheet')}>
              <select className={fieldClass} value="a4_6" disabled>
                <option value="a4_6">{t('admin.stickers.sheetA4')}</option>
              </select>
            </Field>
          </div>

          <div className="flex flex-col gap-1.5">
            <SwitchRow label={t('admin.stickers.cropMarks')} checked={cropMarks} onChange={setCropMarks} />
            <SwitchRow
              label={t('admin.stickers.includeRoomsLockers')}
              checked={includeRoomsLockers}
              onChange={setIncludeRoomsLockers}
            />
          </div>

          <p className="bg-fog rounded-input text-text px-3 py-2.5 text-[13px] leading-[1.45]">
            {t('admin.stickers.rule')}
          </p>

          <div className="mt-auto flex gap-2">
            <Button size="md" type="submit" className="flex-1" loading={create.isPending} disabled={nothing}>
              <Icon name="sticker" size={16} />
              {nothing ? t('admin.stickers.nothing') : t('admin.stickers.create', { sheets })}
            </Button>
            <Button size="md" type="button" variant="secondary" onClick={close}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-center bg-[#e9eef0] p-7">
          <SheetPreview
            stickers={chosen.slice(0, PER_SHEET)}
            cropMarks={cropMarks}
            house={locationName.toUpperCase()}
            caption={t('admin.stickers.sheetHeader', {
              plan: locationName,
              version,
              date: formatDayShort(todayBerlin()),
              sheets: Math.max(1, sheets),
            })}
          />
        </div>
      </form>
    </div>
  );
}

/**
 * § 8's right-hand side: sheet 1, as it will print.
 *
 * A replica rather than a render of the real PDF — the binding document is the
 * server's, and asking for it on every keystroke would cost a Chromium start
 * per click. The QR is generated here from the same `${SITE_URL}/p/<deskCode>`
 * the server encodes, which is what makes this a preview rather than a
 * drawing: a desk whose code never made it into the database shows an empty
 * square here, before 60 stickers are printed with it.
 */
function SheetPreview({
  stickers,
  cropMarks,
  caption,
  house,
}: {
  stickers: Printable[];
  cropMarks: boolean;
  caption: string;
  /** The sticker's eyebrow, upper-cased exactly as the PDF template does. */
  house: string;
}) {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    void Promise.all(
      stickers.map(async (sticker) => {
        if (!sticker.deskCode) return [sticker.resourceId, ''] as const;
        const url = `${import.meta.env.VITE_SITE_URL ?? ''}/p/${sticker.deskCode}`;
        return [sticker.resourceId, await QRCode.toDataURL(url, { margin: 0, width: 128 })] as const;
      }),
    ).then((pairs) => {
      if (live) setCodes(Object.fromEntries(pairs));
    });
    return () => {
      live = false;
    };
  }, [stickers]);

  return (
    <figure className="relative m-0 grid h-[562px] w-[397px] grid-cols-[repeat(2,132px)] grid-rows-[repeat(3,132px)] content-center justify-center gap-5 bg-white shadow-[0_10px_30px_rgba(21,21,21,0.15)]">
      <figcaption className="text-placeholder absolute left-3 top-2.5 text-[9px]">{caption}</figcaption>
      {stickers.map((sticker) => (
        <div
          key={sticker.resourceId}
          className={`border-teal flex flex-col items-center justify-between rounded-[11px] border-[3px] px-2 pb-1.5 pt-[7px] ${
            cropMarks ? 'outline-line outline-dashed outline-1 outline-offset-2' : ''
          }`}
        >
          <div className="flex w-full items-center justify-between">
            <Wordmark size={9} className="text-teal" />
            <span className="text-muted text-[5px] font-semibold tracking-[0.1em]">{house}</span>
          </div>
          {/* The `DeskSticker` artboard, as the server's PDF draws it: the QR
              beside the number, not above it. */}
          <div className="flex items-center gap-[7px]">
            {codes[sticker.resourceId] ? (
              <img src={codes[sticker.resourceId]} alt="" className="h-[52px] w-[52px] flex-none" />
            ) : (
              <span className="bg-fog block h-[52px] w-[52px] flex-none" />
            )}
            <div className="flex flex-col gap-px">
              <span className="text-muted text-[5.5px] font-semibold uppercase tracking-[0.1em]">
                {t('admin.stickers.previewDesk')}
              </span>
              <span className="font-heading text-ink text-[28px] font-bold leading-[0.95] tracking-[-0.03em]">
                {sticker.label}
              </span>
            </div>
          </div>
          <span className="text-teal-dark flex items-center gap-[3px] text-[6px] font-semibold leading-tight">
            <Icon name="sticker" size={7} />
            {t('admin.stickers.previewHint')}
          </span>
        </div>
      ))}
    </figure>
  );
}
